/* eslint-disable complexity */
import { type ProductDuration } from '@essent/common';
import {
  IncentiveType,
  type GetOfferDuration,
  type Offer,
  type OfferOverview,
} from '@innogy/become-a-customer/shared/interfaces';
import {
  ContractType,
  type EnergyType,
} from '@innogy/server/get-offer/interfaces';
import type {
  Marketing_GetNextBestOfferResponseOfferIncentiveV3,
  GetProductTariffsResponseV3,
  GetTariffsResponseProductV3,
  GetTariffsTotalAmounts,
  OfferContentDuration,
  OfferContentIncentive,
  OfferContentProduct,
  Marketing_OfferWithEstimatesV3,
} from '@integration/api-client/marketing';

const BBA_CONSTRAINTS = {
  HOUSEHOLD: { lower: 0, upper: 10 },
  BUSINESS: { lower: 25, upper: 25 },
};

/**
 * Adjusts the budget bill amount (BBA) based on constraints.
 */
export const getAdjustedBBA = (
  vatIncluded: boolean,
  amount: number
): number | undefined => {
  const { lower, upper } = !vatIncluded
    ? BBA_CONSTRAINTS.BUSINESS
    : BBA_CONSTRAINTS.HOUSEHOLD;

  if (amount < lower) {
    return lower;
  }

  if (amount < upper) {
    return upper;
  }

  return amount;
};

/**
 * Calculates the yearly discount amount.
 */
export const calculateYearlyDiscount = (
  tariffs: GetTariffsTotalAmounts,
  incentiveValue: number
): number => {
  return tariffs.yearly - incentiveValue;
};

/**
 * Calculates the monthly discount amount based on segment and incentive.
 */
export const calculateMonthlyDiscount = (
  tariffs: GetTariffsTotalAmounts,
  cashback: number,
  vatIncluded: boolean
): number => {
  if (cashback > 0) {
    return tariffs.monthly - parseFloat((cashback / 12).toFixed(6));
  }

  if (!vatIncluded) {
    return tariffs.monthly;
  }

  return tariffs.proposedMonthlyInstallment;
};

/**
 * Extracts incentive value from content.
 */
export const getCashbackAmount = (content: OfferContentIncentive): number =>
  content.incentiveType === IncentiveType.Cashback
    ? (content.incentiveValue ?? 0)
    : 0;

/**
 * Calculates discount details.
 */
export const calculateDiscount = (
  tariffs: GetProductTariffsResponseV3,
  content: OfferContentIncentive
) => {
  const cashback = getCashbackAmount(content);
  const amounts = tariffs?.totalAmounts ?? 0;

  return {
    discountedYearlyAmount: calculateYearlyDiscount(amounts, cashback),
    discountedMonthlyAmount: calculateMonthlyDiscount(
      amounts,
      cashback,
      tariffs?.isVat
    ),
    discountPrice: cashback,
  };
};

/**
 * Maps tariff response to legacy OfferOverview structure.
 */
export const mapToLegacyOfferOverviews = (
  offerV4Products: GetTariffsResponseProductV3[],
  incentives: Marketing_GetNextBestOfferResponseOfferIncentiveV3[]
): OfferOverview[] =>
  offerV4Products.map((product) => ({
    energyType: product.type as EnergyType,
    consumptionPrices:
      product.consumptionPrices?.map((consumption) => ({
        amount: consumption.amount,
        description: consumption.description,
        fromDate: consumption.fromDate,
        toDate: consumption.toDate,
      })) ?? [],
    priceGroups: product.priceGroups.map((priceGroup) => ({
      id: priceGroup.id,
      description: priceGroup.description,
      prices: priceGroup.prices.map((price) => ({
        id: price.id,
        unit: price.unit.replace('€/', ''),
        unitPrice: price.unitPrice,
        description: price.description.replace(/&nbsp;/g, '\u00A0').trim(),
        expectedPeriodAmount: price.periodPrice,
        fromDate: price.fromDate as string,
        toDate: price.toDate as string,
      })),
      totalPrice: {
        expectedMonthlyAmount: priceGroup.totalPrice.monthly,
        expectedYearlyAmount: priceGroup.totalPrice.yearly,
      },
    })),
    totalPrice: {
      expectedMonthlyAmount: product.totalPrice.monthly,
      expectedYearlyAmount: product.totalPrice.yearly,
    },
    financialIncentives: incentives.map((incentive) => ({
      id: incentive.incentiveId,
      description: incentive.description ?? '',
    })),
  }));

/**
 * Maps OfferWithEstimatesV3 to a legacy Offer format.
 */
export const mapOfferWithEstimatesToLegacyOffer = (
  offerV4: Marketing_OfferWithEstimatesV3
): Offer => {
  // Bug: API returns object, but model expects array, handled both here
  const scContent = Array.isArray(offerV4.content)
    ? offerV4.content[0]
    : (offerV4.content ?? null);

  const productInfo = scContent?.product ?? ({} as OfferContentProduct);
  const durationInfo = scContent?.duration ?? ({} as OfferContentDuration);
  const incentiveInfo = scContent?.incentive ?? ({} as OfferContentIncentive);

  // Bug: API returns object, but model expects array, handled both here
  const productTariff = Array.isArray(offerV4.productTariffs)
    ? offerV4.productTariffs[0]
    : (offerV4.productTariffs ?? null);
  const incentive = offerV4.incentives?.[0] ?? null;
  let contractType: `${ContractType}` | undefined = ContractType.FIXED;

  if (productTariff?.products[0]) {
    contractType = productTariff?.products[0].contractType;
  }

  const discounts = calculateDiscount(productTariff, incentiveInfo);

  return {
    offerId: offerV4.offerId,
    actionConditionsUrl: incentiveInfo.actionConditionsUrl ?? '',
    campaignUsp: productInfo.campaignUsp ?? '',
    customUsp: productInfo.customUsp ?? '',
    campaignId: offerV4.cdtId,
    duration: offerV4.duration as ProductDuration,
    durationTitle:
      contractType === ContractType.DYNAMIC
        ? productInfo.dynamicPricingTitle
        : (durationInfo.durationTitle ?? ''),
    vatIncluded: productTariff?.isVat ?? false,
    beforeDiscountExpectedMonthlyAmount: productTariff?.totalAmounts.monthly,
    beforeDiscountExpectedYearlyAmount: productTariff?.totalAmounts.yearly,
    expectedMonthlyAmount: discounts.discountedMonthlyAmount,
    incentiveId: incentive?.cdtIncentiveId ?? '',
    disclaimerContent: productInfo?.disclaimerContent ?? '',
    productTitle: productInfo?.productTitle ?? '',
    productSubtitle: productInfo?.productSubtitle ?? '',
    isHighlighted: productInfo?.isHighlighted ?? false,
    isHighlightedLabel: productInfo?.isHighlightedLabel ?? '',
    dynamicPricingTitle: productInfo?.dynamicPricingTitle ?? '',
    generalTermsAndConditionsUrl:
      productInfo.generalTermsAndConditionsUrl ?? '',
    incentiveType:
      (incentiveInfo?.incentiveType as IncentiveType) ?? IncentiveType.Cashback,
    incentiveFullDescription: incentiveInfo.incentiveFullDescription ?? '',
    incentiveImage: incentiveInfo.incentiveImage ?? '',
    incentiveInfoUrl: incentiveInfo.incentiveInfoUrl ?? '',
    incentiveSummary: incentiveInfo.incentiveSummary ?? '',
    incentiveTitle: incentiveInfo.incentiveTitle ?? '',
    incentiveUsp: incentiveInfo.incentiveUsp ?? '',
    incentiveValue: incentiveInfo.incentiveValue ?? 0,
    moreInfoCtaButtonText: productInfo.moreInfoCtaButtonText ?? '',
    moreInfoTitle: productInfo.moreInfoTitle ?? '',
    moreInfoUrl: productInfo.moreInfoUrl ?? '',
    noIncentiveFallbackInfoUrl: productInfo.noIncentiveFallbackInfoUrl ?? '',
    noIncentiveFallbackUsp: productInfo.noIncentiveFallbackUsp ?? '',
    productConditionsUrl: productInfo.productConditionsUrl ?? '',
    qualityCriteriaUrl: productInfo.qualityCriteriaUrl ?? '',
    retentionIncentive: productInfo.retentionIncentive ?? '',
    budgetBillAmount: getAdjustedBBA(
      productTariff?.isVat,
      productTariff?.totalAmounts.proposedMonthlyInstallment ?? 0
    ),
    usps: [
      productInfo?.campaignUsp,
      incentiveInfo?.incentiveUsp || productInfo?.noIncentiveFallbackUsp,
      productInfo?.customUsp,
    ].filter(Boolean) as string[],
    expectedYearlyAmount: discounts.discountedYearlyAmount,
    discountPrice: discounts.discountPrice,
    commodities: (productTariff?.products || []).map(
      (product) => product.type
    ) as EnergyType[],
    isDynamicProduct: contractType === ContractType.DYNAMIC,
    contractType: contractType ?? ContractType.FIXED,
    offerOverviews: mapToLegacyOfferOverviews(
      productTariff?.products ?? [],
      offerV4.incentives
    ),
  };
};

export const mapDurationsFromOffersWithEstimates = (
  offer: Marketing_OfferWithEstimatesV3
): GetOfferDuration => {
  const scContent = offer.content?.[0] ?? undefined;
  const duration: GetOfferDuration = {
    duration: offer.duration as ProductDuration,
    offerId: offer.offerId,
    durationTitle: scContent?.duration?.durationTitle ?? '',
  };
  return duration;
};
